import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { useFeatureSettings } from '@/hooks/use-feature-settings';
import { useCompliancePermissions } from '@/hooks/use-permissions';
import { complianceService } from '@/services/compliance-service';
import type { ComplianceCheck, CreateComplianceCheck, UpdateComplianceCheck } from '@/types/compliance';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Save, 
  X, 
  ExternalLink,
  ChevronRight,
  Search,
  Building2,
  Package,
  CheckCircle2,
  Shield,
  Calendar,
  Trash2,
  Users,
  User,
  Plus,
  Loader2,
  DollarSign,
  MapPin,
  FileText,
  BookOpen,
  Check,
} from 'lucide-react';
// BarChart3 removed - not currently used
import { cn } from '@/lib/utils';

// Types
type ComplianceRegimeCode = 'PUWER' | 'LOLER' | 'PAT' | 'EICR' | 'MOT' | 'GDPR' | 'ISO9001' | 'ISO14001' | 'ISO27001' | 'ISO45001';
type IntervalUnit = 'days' | 'months' | 'years';
type AssignmentType = 'asset_owner' | 'team' | 'user';

interface ComplianceRegime {
  code: ComplianceRegimeCode;
  name: string;
  description: string;
  category: string;
}

interface RegimeSettings {
  enabled: boolean;
  complianceType: string;
  defaultInterval: number;
  intervalUnit: IntervalUnit;
  gracePeriod: number;
  gracePeriodUnit: IntervalUnit;
  minAssetValue: number | null;
  maxAssetValue: number | null;
  regulatoryBody: string;
  legislation: string;
  guidanceUrl: string;
  enabledCategoryIds: string[];
  enabledLocationIds: string[];
  requirements: Array<{ id: string; text: string; sortOrder: number }>;
  assignmentType: AssignmentType;
  assignedTeamId: string | null;
  assignedUserId: string | null;
  requiredDocuments: Array<{ id: string; documentType: string; mandatory: boolean; sortOrder: number }>;
  complianceScore: number;
  requireDocumentUpload: boolean;
  requireCertificate: boolean;
  requireReport: boolean;
  requirePhoto: boolean;
  requireNotes: boolean;
  requireCostEstimate: boolean;
  requireContractorDetails: boolean;
  requireNextDueDate: boolean;
}

interface Category {
  id: string;
  name: string;
  assetCount: number;
  parentId?: string;
  color?: string;
}

interface Location {
  id: string;
  name: string;
  address: string;
  assetCount: number;
  parentId?: string;
}

// Mock data
const COMPLIANCE_REGIMES: Record<ComplianceRegimeCode, ComplianceRegime> = {
  PUWER: {
    code: 'PUWER',
    name: 'Work Equipment Safety (PUWER)',
    description: 'Provision and Use of Work Equipment Regulations',
    category: 'Health & Safety',
  },
  LOLER: {
    code: 'LOLER',
    name: 'Lifting Operations (LOLER)',
    description: 'Lifting Operations and Lifting Equipment Regulations',
    category: 'Health & Safety',
  },
  PAT: {
    code: 'PAT',
    name: 'Portable Appliance Testing',
    description: 'Electrical safety testing for portable appliances',
    category: 'Health & Safety',
  },
  EICR: {
    code: 'EICR',
    name: 'Electrical Installation Condition Report',
    description: 'Fixed electrical installation testing',
    category: 'Health & Safety',
  },
  MOT: {
    code: 'MOT',
    name: 'MOT Testing',
    description: 'Ministry of Transport vehicle safety testing',
    category: 'Vehicle Compliance',
  },
  GDPR: {
    code: 'GDPR',
    name: 'Data Protection (GDPR)',
    description: 'General Data Protection Regulation compliance',
    category: 'Data & Privacy',
  },
  ISO9001: {
    code: 'ISO9001',
    name: 'Quality Management (ISO 9001)',
    description: 'Quality management system standard',
    category: 'ISO Standards',
  },
  ISO14001: {
    code: 'ISO14001',
    name: 'Environmental Management (ISO 14001)',
    description: 'Environmental management system standard',
    category: 'ISO Standards',
  },
  ISO27001: {
    code: 'ISO27001',
    name: 'Information Security (ISO 27001)',
    description: 'Information security management standard',
    category: 'ISO Standards',
  },
  ISO45001: {
    code: 'ISO45001',
    name: 'Occupational Health & Safety (ISO 45001)',
    description: 'Occupational health and safety management standard',
    category: 'ISO Standards',
  },
};

const MOCK_CATEGORIES: Category[] = [
  { id: 'cat-1', name: 'Machinery & Equipment', assetCount: 45 },
  { id: 'cat-1-1', name: 'Industrial Machinery', assetCount: 25, parentId: 'cat-1' },
  { id: 'cat-1-2', name: 'Workshop Tools', assetCount: 20, parentId: 'cat-1' },
  { id: 'cat-2', name: 'IT Equipment', assetCount: 120 },
  { id: 'cat-2-1', name: 'Computers & Laptops', assetCount: 80, parentId: 'cat-2' },
  { id: 'cat-2-2', name: 'Servers', assetCount: 15, parentId: 'cat-2' },
  { id: 'cat-2-3', name: 'Peripherals', assetCount: 25, parentId: 'cat-2' },
  { id: 'cat-3', name: 'Vehicles', assetCount: 18 },
  { id: 'cat-3-1', name: 'Company Cars', assetCount: 12, parentId: 'cat-3' },
  { id: 'cat-3-2', name: 'Vans', assetCount: 6, parentId: 'cat-3' },
  { id: 'cat-4', name: 'Furniture & Fixtures', assetCount: 200 },
  { id: 'cat-5', name: 'Lifting Equipment', assetCount: 8 },
];

const MOCK_LOCATIONS: Location[] = [
  { id: 'loc-1', name: 'Head Office', address: '123 Business Park, London, UK', assetCount: 250 },
  { id: 'loc-1-1', name: 'Ground Floor', address: '123 Business Park, London, UK', assetCount: 100, parentId: 'loc-1' },
  { id: 'loc-1-2', name: 'First Floor', address: '123 Business Park, London, UK', assetCount: 80, parentId: 'loc-1' },
  { id: 'loc-1-3', name: 'Second Floor', address: '123 Business Park, London, UK', assetCount: 70, parentId: 'loc-1' },
  { id: 'loc-2', name: 'Manchester Warehouse', address: '45 Industrial Estate, Manchester, UK', assetCount: 85 },
  { id: 'loc-2-1', name: 'Storage Area A', address: '45 Industrial Estate, Manchester, UK', assetCount: 40, parentId: 'loc-2' },
  { id: 'loc-2-2', name: 'Storage Area B', address: '45 Industrial Estate, Manchester, UK', assetCount: 45, parentId: 'loc-2' },
  { id: 'loc-3', name: 'Birmingham Office', address: '78 City Centre, Birmingham, UK', assetCount: 42 },
  { id: 'loc-4', name: 'Remote Sites', address: 'Various Locations', assetCount: 14 },
];

const MOCK_TEAMS = [
  { id: 'team-1', name: 'Maintenance Team' },
  { id: 'team-2', name: 'Facilities Team' },
  { id: 'team-3', name: 'Health & Safety Team' },
  { id: 'team-4', name: 'Operations Team' },
];

const MOCK_USERS = [
  { id: 'user-1', name: 'John Smith' },
  { id: 'user-2', name: 'Sarah Johnson' },
  { id: 'user-3', name: 'Mike Williams' },
];

export default function ComplianceRegulationsPage(): React.ReactElement {
  const { modules } = useFeatureSettings();
  const complianceModule = modules.compliance;
  const isDemoMode = complianceModule?.mode === 'demo';
  const { toast } = useToast();
  const { canManage: canManageCompliance } = useCompliancePermissions();
  
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedRegime, setSelectedRegime] = useState<ComplianceRegimeCode>('PUWER');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  const [categorySearch, setCategorySearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingRegime, setPendingRegime] = useState<ComplianceRegimeCode | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // API state for live mode
  const [apiComplianceChecks, setApiComplianceChecks] = useState<ComplianceCheck[]>([]);
  const [selectedCheckId, setSelectedCheckId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newCheckName, setNewCheckName] = useState('');
  const [newCheckType, setNewCheckType] = useState('');
  const [newCheckDescription, setNewCheckDescription] = useState('');
  const [newCheckFrequency, setNewCheckFrequency] = useState('annual');
  const [newCheckInterval, setNewCheckInterval] = useState(12);
  const [newCheckIntervalUnit, setNewCheckIntervalUnit] = useState<IntervalUnit>('months');
  const [newCheckLeadTime, setNewCheckLeadTime] = useState(30);
  const [newCheckLeadTimeUnit, setNewCheckLeadTimeUnit] = useState<IntervalUnit>('days');
  const [newCheckPriority, setNewCheckPriority] = useState('medium');
  const [newCheckRegulatoryBody, setNewCheckRegulatoryBody] = useState('');
  const [newCheckLegislation, setNewCheckLegislation] = useState('');
  const [newCheckGuidanceUrl, setNewCheckGuidanceUrl] = useState('');
  const [newCheckMinValue, setNewCheckMinValue] = useState<number | null>(null);
  const [newCheckMaxValue, setNewCheckMaxValue] = useState<number | null>(null);
  const [newCheckEnabled, setNewCheckEnabled] = useState(true);
  const [newCheckComplianceType, setNewCheckComplianceType] = useState('safety-inspection');
  const [newCheckRequireCertificate, setNewCheckRequireCertificate] = useState(false);
  const [newCheckRequireReport, setNewCheckRequireReport] = useState(false);
  const [newCheckRequirePhoto, setNewCheckRequirePhoto] = useState(false);
  const [newCheckRequireNotes, setNewCheckRequireNotes] = useState(false);
  const [newCheckRequireCostEstimate, setNewCheckRequireCostEstimate] = useState(false);
  const [newCheckRequireContractorDetails, setNewCheckRequireContractorDetails] = useState(false);
  const [newCheckRequireNextDueDate, setNewCheckRequireNextDueDate] = useState(true);
  const [newCheckActiveTab, setNewCheckActiveTab] = useState('details');
  const [newCheckCategoryIds, setNewCheckCategoryIds] = useState<string[]>([]);
  const [newCheckLocationIds, setNewCheckLocationIds] = useState<string[]>([]);
  const [newCheckCategorySearch, setNewCheckCategorySearch] = useState('');
  const [newCheckLocationSearch, setNewCheckLocationSearch] = useState('');
  
  // Fetch compliance checks from API in live mode
  const fetchComplianceChecks = useCallback(async () => {
    if (isDemoMode) return;
    
    setIsLoading(true);
    try {
      const checks = await complianceService.getComplianceChecks();
      setApiComplianceChecks(checks);
      // Select first check if none selected
      if (checks.length > 0 && !selectedCheckId) {
        setSelectedCheckId(checks[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch compliance checks:', error);
      toast({
        title: 'Error',
        description: 'Failed to load compliance checks',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [isDemoMode, selectedCheckId, toast]);
  
  useEffect(() => {
    if (!isDemoMode) {
      fetchComplianceChecks();
    }
  }, [isDemoMode, fetchComplianceChecks]);
  
  // Get the currently selected compliance check from API data
  const selectedApiCheck = apiComplianceChecks.find(c => c.id === selectedCheckId);
  
  // Helper to convert API frequency to interval
  const parseFrequency = (frequency: string): { interval: number; unit: IntervalUnit } => {
    const freq = frequency?.toLowerCase() || 'annual';
    if (freq === 'daily') return { interval: 1, unit: 'days' };
    if (freq === 'weekly') return { interval: 7, unit: 'days' };
    if (freq === 'monthly') return { interval: 1, unit: 'months' };
    if (freq === 'quarterly') return { interval: 3, unit: 'months' };
    if (freq === 'semi-annual' || freq === 'semi-annually') return { interval: 6, unit: 'months' };
    if (freq === 'annual' || freq === 'annually') return { interval: 12, unit: 'months' };
    if (freq === '5-years') return { interval: 5, unit: 'years' };
    return { interval: 12, unit: 'months' };
  };
  
  // Convert interval back to frequency string for API
  const toFrequencyString = (interval: number, unit: IntervalUnit): string => {
    if (unit === 'days' && interval === 1) return 'daily';
    if (unit === 'days' && interval === 7) return 'weekly';
    if (unit === 'months' && interval === 1) return 'monthly';
    if (unit === 'months' && interval === 3) return 'quarterly';
    if (unit === 'months' && interval === 6) return 'semi-annual';
    if (unit === 'months' && interval === 12) return 'annual';
    if (unit === 'years' && interval === 5) return '5-years';
    return 'annual';
  };

  // Initialize default settings for all regimes
  const getDefaultSettings = (): Record<ComplianceRegimeCode, RegimeSettings> => {
    const allRegimes = Object.keys(COMPLIANCE_REGIMES) as ComplianceRegimeCode[];
    const settings: Partial<Record<ComplianceRegimeCode, RegimeSettings>> = {};
    
    // Define specific settings for known regimes
    const specificSettings: Partial<Record<ComplianceRegimeCode, RegimeSettings>> = {
      PUWER: {
        enabled: true,
        complianceType: 'safety-inspection',
        defaultInterval: 6,
        intervalUnit: 'months',
        gracePeriod: 7,
        gracePeriodUnit: 'days',
        minAssetValue: null,
        maxAssetValue: null,
        regulatoryBody: 'HSE (Health and Safety Executive)',
        legislation: 'PUWER 1998 reg. 6',
        guidanceUrl: 'https://www.hse.gov.uk/work-equipment-machinery/puwer.htm',
        enabledCategoryIds: ['cat-1', 'cat-1-1', 'cat-1-2', 'cat-5'],
        enabledLocationIds: ['loc-1', 'loc-2', 'loc-3'],
        requirements: [
          { id: 'req-1', text: 'Equipment is suitable for intended use', sortOrder: 1 },
          { id: 'req-2', text: 'Equipment is maintained in safe working order', sortOrder: 2 },
          { id: 'req-3', text: 'Inspection records are up to date', sortOrder: 3 },
        ],
        assignmentType: 'asset_owner',
        assignedTeamId: null,
        assignedUserId: null,
        requiredDocuments: [
          { id: 'doc-1', documentType: 'Inspection Report', mandatory: true, sortOrder: 1 },
          { id: 'doc-2', documentType: 'Photo Evidence', mandatory: false, sortOrder: 2 },
        ],
        complianceScore: 87,
        requireDocumentUpload: true,
        requireCertificate: true,
        requireReport: true,
        requirePhoto: false,
        requireNotes: true,
        requireCostEstimate: false,
        requireContractorDetails: true,
        requireNextDueDate: true,
      },
      LOLER: {
        enabled: true,
        complianceType: 'safety-inspection',
        defaultInterval: 6,
        intervalUnit: 'months',
        gracePeriod: 3,
        gracePeriodUnit: 'days',
        minAssetValue: null,
        maxAssetValue: null,
        regulatoryBody: 'HSE (Health and Safety Executive)',
        legislation: 'LOLER 1998',
        guidanceUrl: 'https://www.hse.gov.uk/work-equipment-machinery/loler.htm',
        enabledCategoryIds: ['cat-1', 'cat-1-1', 'cat-1-2'],
        enabledLocationIds: ['loc-1', 'loc-2'],
        requirements: [
          { id: 'req-1', text: 'Lifting equipment is fit for purpose', sortOrder: 1 },
          { id: 'req-2', text: 'Safe working load is clearly marked', sortOrder: 2 },
        ],
        assignmentType: 'team',
        assignedTeamId: 'team-3',
        assignedUserId: null,
        requiredDocuments: [
          { id: 'doc-1', documentType: 'Thorough Examination Report', mandatory: true, sortOrder: 1 },
        ],
        complianceScore: 92,
        requireDocumentUpload: true,
        requireCertificate: true,
        requireReport: true,
        requirePhoto: false,
        requireNotes: true,
        requireCostEstimate: false,
        requireContractorDetails: true,
        requireNextDueDate: true,
      },
      PAT: {
        enabled: true,
        complianceType: 'safety-inspection',
        defaultInterval: 12,
        intervalUnit: 'months',
        gracePeriod: 14,
        gracePeriodUnit: 'days',
        minAssetValue: null,
        maxAssetValue: null,
        regulatoryBody: 'HSE (Health and Safety Executive)',
        legislation: 'Electricity at Work Regulations 1989',
        guidanceUrl: 'https://www.hse.gov.uk/electricity/pat-testing.htm',
        enabledCategoryIds: ['cat-2', 'cat-2-1', 'cat-2-3'],
        enabledLocationIds: ['loc-1', 'loc-4'],
        requirements: [
          { id: 'req-1', text: 'Visual inspection completed', sortOrder: 1 },
          { id: 'req-2', text: 'Electrical safety tests passed', sortOrder: 2 },
        ],
        assignmentType: 'user',
        assignedTeamId: null,
        assignedUserId: 'user-2',
        requiredDocuments: [
          { id: 'doc-1', documentType: 'PAT Test Certificate', mandatory: true, sortOrder: 1 },
        ],
        complianceScore: 78,
        requireDocumentUpload: true,
        requireCertificate: true,
        requireReport: false,
        requirePhoto: false,
        requireNotes: false,
        requireCostEstimate: false,
        requireContractorDetails: true,
        requireNextDueDate: true,
      },
      EICR: {
        enabled: true,
        complianceType: 'safety-inspection',
        defaultInterval: 5,
        intervalUnit: 'years',
        gracePeriod: 30,
        gracePeriodUnit: 'days',
        minAssetValue: null,
        maxAssetValue: null,
        regulatoryBody: 'NICEIC',
        legislation: 'BS 7671 Wiring Regulations',
        guidanceUrl: 'https://www.niceic.com/electrician/eicr',
        enabledCategoryIds: ['cat-2'],
        enabledLocationIds: ['loc-1', 'loc-2', 'loc-3', 'loc-4'],
        requirements: [
          { id: 'req-1', text: 'Fixed electrical installation inspected', sortOrder: 1 },
        ],
        assignmentType: 'team',
        assignedTeamId: 'team-2',
        assignedUserId: null,
        requiredDocuments: [
          { id: 'doc-1', documentType: 'EICR Certificate', mandatory: true, sortOrder: 1 },
        ],
        complianceScore: 95,
        requireDocumentUpload: true,
        requireCertificate: true,
        requireReport: false,
        requirePhoto: false,
        requireNotes: false,
        requireCostEstimate: false,
        requireContractorDetails: true,
        requireNextDueDate: true,
      },
      MOT: {
        enabled: true,
        complianceType: 'safety-inspection',
        defaultInterval: 1,
        intervalUnit: 'years',
        gracePeriod: 0,
        gracePeriodUnit: 'days',
        minAssetValue: null,
        maxAssetValue: null,
        regulatoryBody: 'DVSA (Driver and Vehicle Standards Agency)',
        legislation: 'Road Traffic Act 1988',
        guidanceUrl: 'https://www.gov.uk/getting-an-mot',
        enabledCategoryIds: ['cat-3'],
        enabledLocationIds: ['loc-1', 'loc-2'],
        requirements: [
          { id: 'req-1', text: 'Vehicle roadworthiness verified', sortOrder: 1 },
        ],
        assignmentType: 'asset_owner',
        assignedTeamId: null,
        assignedUserId: null,
        requiredDocuments: [
          { id: 'doc-1', documentType: 'MOT Certificate', mandatory: true, sortOrder: 1 },
        ],
        complianceScore: 100,
        requireDocumentUpload: true,
        requireCertificate: true,
        requireReport: false,
        requirePhoto: false,
        requireNotes: false,
        requireCostEstimate: false,
        requireContractorDetails: false,
        requireNextDueDate: true,
      },
    };

    // Create default settings for all regimes
    allRegimes.forEach(regime => {
      settings[regime] = specificSettings[regime] || {
        enabled: false,
        complianceType: 'safety-inspection',
        defaultInterval: 12,
        intervalUnit: 'months',
        gracePeriod: 7,
        gracePeriodUnit: 'days',
        minAssetValue: null,
        maxAssetValue: null,
        regulatoryBody: '',
        legislation: '',
        guidanceUrl: '',
        enabledCategoryIds: [],
        enabledLocationIds: [],
        requirements: [],
        assignmentType: 'asset_owner',
        assignedTeamId: null,
        assignedUserId: null,
        requiredDocuments: [],
        complianceScore: 0,
        requireDocumentUpload: true,
        requireCertificate: false,
        requireReport: false,
        requirePhoto: false,
        requireNotes: false,
        requireCostEstimate: false,
        requireContractorDetails: false,
        requireNextDueDate: true,
      };
    });

    return settings as Record<ComplianceRegimeCode, RegimeSettings>;
  };

  const [regimeSettings, setRegimeSettings] = useState<Record<ComplianceRegimeCode, RegimeSettings>>(getDefaultSettings());
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingName, setEditingName] = useState<string>('');

  const currentSettings = regimeSettings[selectedRegime];
  const currentRegime = COMPLIANCE_REGIMES[selectedRegime];

  const updateSettings = (updates: Partial<RegimeSettings>) => {
    setRegimeSettings(prev => ({
      ...prev,
      [selectedRegime]: { ...prev[selectedRegime], ...updates },
    }));
    setHasChanges(true);
    setIsEditMode(true);
  };

  const handleSave = async () => {
    if (isDemoMode) {
      // Demo mode: just simulate save
      console.log('Saving settings (demo mode):', regimeSettings[selectedRegime]);
      await new Promise(resolve => setTimeout(resolve, 500));
      toast({
        title: 'Demo Mode',
        description: 'In demo mode, changes are not saved. This rule would be updated in live mode.',
      });
      setHasChanges(false);
      setIsEditMode(false);
      return true;
    }
    
    // Live mode: call API
    if (!selectedCheckId || !selectedApiCheck) {
      toast({
        title: 'Error',
        description: 'No compliance check selected',
        variant: 'destructive',
      });
      return false;
    }
    
    setIsSaving(true);
    try {
      const settings = regimeSettings[selectedRegime];
      const updateData: UpdateComplianceCheck = {
        name: editingName || selectedApiCheck.name,
        description: selectedApiCheck.description,
        category: selectedApiCheck.category,
        regulation: selectedApiCheck.regulation,
        regulatoryBody: settings.regulatoryBody,
        frequency: toFrequencyString(settings.defaultInterval, settings.intervalUnit),
        leadTime: settings.gracePeriod,
        notificationLeadDays: settings.gracePeriod,
        escalationDays: 7,
        priority: selectedApiCheck.priority,
        requiresEvidence: settings.requiredDocuments.some(d => d.mandatory),
        requiresCost: selectedApiCheck.requiresCost,
        documentationRequired: settings.requiredDocuments.length > 0,
        isDefault: selectedApiCheck.isDefault,
        autoAssignToNewAssets: selectedApiCheck.autoAssignToNewAssets,
        applicableToCategories: settings.enabledCategoryIds,
        applicableToLocations: settings.enabledLocationIds,
        active: settings.enabled,
      };
      
      await complianceService.updateComplianceCheck(selectedCheckId, updateData);
      
      // Refresh the list
      await fetchComplianceChecks();
      
      toast({
        title: 'Success',
        description: 'Compliance check updated successfully',
      });
      
      setHasChanges(false);
      setIsEditMode(false);
      return true;
    } catch (error) {
      console.error('Failed to save compliance check:', error);
      toast({
        title: 'Error',
        description: 'Failed to save compliance check',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setRegimeSettings(getDefaultSettings());
    setHasChanges(false);
  };

  const handleRegimeChange = (regime: ComplianceRegimeCode) => {
    // If in edit mode and has changes, show confirmation dialog
    if (isEditMode && hasChanges) {
      setPendingRegime(regime);
      setShowConfirmDialog(true);
    } else {
      // No changes or not in edit mode, change directly
      setSelectedRegime(regime);
      setIsEditMode(false);
      setHasChanges(false);
    }
  };

  const handleConfirmSave = async () => {
    const success = await handleSave();
    if (success && pendingRegime) {
      setSelectedRegime(pendingRegime);
      setPendingRegime(null);
      setShowConfirmDialog(false);
    }
  };

  const handleDiscardChanges = () => {
    if (pendingRegime) {
      setSelectedRegime(pendingRegime);
      setPendingRegime(null);
    }
    setHasChanges(false);
    setIsEditMode(false);
    setShowConfirmDialog(false);
  };

  const handleCancelChange = () => {
    setPendingRegime(null);
    setShowConfirmDialog(false);
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (isDemoMode) {
      // Demo mode: just simulate delete
      console.log('Deleting regulation (demo mode):', selectedRegime);
      setShowDeleteDialog(false);
      return;
    }
    
    // Live mode: call API
    if (!selectedCheckId) {
      toast({
        title: 'Error',
        description: 'No compliance check selected',
        variant: 'destructive',
      });
      setShowDeleteDialog(false);
      return;
    }
    
    setIsSaving(true);
    try {
      await complianceService.deleteComplianceCheck(selectedCheckId);
      
      // Remove from local state and select next item
      const remainingChecks = apiComplianceChecks.filter(c => c.id !== selectedCheckId);
      setApiComplianceChecks(remainingChecks);
      
      if (remainingChecks.length > 0) {
        setSelectedCheckId(remainingChecks[0].id);
      } else {
        setSelectedCheckId(null);
      }
      
      toast({
        title: 'Success',
        description: 'Compliance check deleted successfully',
      });
      
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Failed to delete compliance check:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete compliance check',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleNameChange = (newName: string) => {
    setEditingName(newName);
    setHasChanges(true);
    setIsEditMode(true);
  };
  
  // Create new compliance check
  const handleCreateCheck = async () => {
    if (!newCheckName.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a name for the compliance check',
        variant: 'destructive',
      });
      return;
    }
    
    setIsSaving(true);
    
    // Demo mode: pretend to save
    if (isDemoMode) {
      try {
        await new Promise(resolve => setTimeout(resolve, 800));
        
        toast({
          title: 'Demo Mode',
          description: 'In demo mode, changes are not saved. This rule would be created in live mode.',
        });
        
        // Reset form
        setShowCreateDialog(false);
        setNewCheckName('');
        setNewCheckType('');
        setNewCheckDescription('');
        setNewCheckFrequency('annual');
        setNewCheckInterval(12);
        setNewCheckIntervalUnit('months');
        setNewCheckLeadTime(30);
        setNewCheckLeadTimeUnit('days');
        setNewCheckPriority('medium');
        setNewCheckRegulatoryBody('');
        setNewCheckLegislation('');
        setNewCheckGuidanceUrl('');
        setNewCheckMinValue(null);
        setNewCheckMaxValue(null);
        setNewCheckEnabled(true);
        setNewCheckComplianceType('safety-inspection');
        setNewCheckRequireCertificate(false);
        setNewCheckRequireReport(false);
        setNewCheckRequirePhoto(false);
        setNewCheckRequireNotes(false);
        setNewCheckRequireCostEstimate(false);
        setNewCheckRequireContractorDetails(false);
        setNewCheckRequireNextDueDate(true);
        setNewCheckActiveTab('details');
        setNewCheckCategoryIds([]);
        setNewCheckLocationIds([]);
        setNewCheckCategorySearch('');
        setNewCheckLocationSearch('');
      } catch (error) {
        console.error('Demo mode error:', error);
      } finally {
        setIsSaving(false);
      }
      return;
    }
    
    // Live mode: call API
    try {
      const createData: CreateComplianceCheck = {
        name: newCheckName.trim(),
        type: newCheckType,
        description: newCheckDescription,
        frequency: toFrequencyString(newCheckInterval, newCheckIntervalUnit),
        leadTime: newCheckLeadTime,
        notificationLeadDays: newCheckLeadTime,
        escalationDays: 7,
        priority: newCheckPriority,
        regulatoryBody: newCheckRegulatoryBody,
        regulation: newCheckLegislation,
        guidanceUrl: newCheckGuidanceUrl,
        minAssetValue: newCheckMinValue,
        maxAssetValue: newCheckMaxValue,
        requiresEvidence: false,
        requiresCost: false,
        documentationRequired: false,
        isDefault: false,
        autoAssignToNewAssets: false,
        applicableToCategories: newCheckCategoryIds,
        applicableToLocations: newCheckLocationIds,
        active: newCheckEnabled,
      };
      
      const newCheck = await complianceService.createComplianceCheck(createData);
      
      // Add to local state and select it
      setApiComplianceChecks(prev => [...prev, newCheck]);
      setSelectedCheckId(newCheck.id);
      
      toast({
        title: 'Success',
        description: 'Compliance check created successfully',
      });
      
      // Reset form
      setShowCreateDialog(false);
      setNewCheckName('');
      setNewCheckType('');
      setNewCheckDescription('');
      setNewCheckFrequency('annual');
      setNewCheckInterval(12);
      setNewCheckIntervalUnit('months');
      setNewCheckLeadTime(30);
      setNewCheckLeadTimeUnit('days');
      setNewCheckPriority('medium');
      setNewCheckRegulatoryBody('');
      setNewCheckLegislation('');
      setNewCheckGuidanceUrl('');
      setNewCheckMinValue(null);
      setNewCheckMaxValue(null);
      setNewCheckEnabled(true);
      setNewCheckComplianceType('safety-inspection');
      setNewCheckRequireCertificate(false);
      setNewCheckRequireReport(false);
      setNewCheckRequirePhoto(false);
      setNewCheckRequireNotes(false);
      setNewCheckRequireCostEstimate(false);
      setNewCheckRequireContractorDetails(false);
      setNewCheckRequireNextDueDate(true);
      setNewCheckActiveTab('details');
      setNewCheckCategoryIds([]);
      setNewCheckLocationIds([]);
      setNewCheckCategorySearch('');
      setNewCheckLocationSearch('');
    } catch (error) {
      console.error('Failed to create compliance check:', error);
      toast({
        title: 'Error',
        description: 'Failed to create compliance check',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  // Handle selecting an API compliance check (live mode)
  const handleSelectApiCheck = (checkId: number) => {
    if (isEditMode && hasChanges) {
      // Store pending selection and show confirm dialog
      toast({
        title: 'Unsaved Changes',
        description: 'Please save or discard your changes before selecting another item',
        variant: 'destructive',
      });
      return;
    }
    setSelectedCheckId(checkId);
    setHasChanges(false);
    setIsEditMode(false);
  };

  // Filter categories and locations
  const filteredRegimes = Object.values(COMPLIANCE_REGIMES).filter(regime => {
    const query = searchQuery.toLowerCase();
    return regime.code.toLowerCase().includes(query) || 
           regime.name.toLowerCase().includes(query);
  });

  const filteredCategories = MOCK_CATEGORIES.filter(cat =>
    cat.name.toLowerCase().includes(categorySearch.toLowerCase())
  );

  const filteredLocations = MOCK_LOCATIONS.filter(loc =>
    loc.name.toLowerCase().includes(locationSearch.toLowerCase()) ||
    loc.address.toLowerCase().includes(locationSearch.toLowerCase())
  );

  const toggleCategory = (categoryId: string) => {
    const newIds = currentSettings.enabledCategoryIds.includes(categoryId)
      ? currentSettings.enabledCategoryIds.filter(id => id !== categoryId)
      : [...currentSettings.enabledCategoryIds, categoryId];
    updateSettings({ enabledCategoryIds: newIds });
  };

  const toggleLocation = (locationId: string) => {
    const newIds = currentSettings.enabledLocationIds.includes(locationId)
      ? currentSettings.enabledLocationIds.filter(id => id !== locationId)
      : [...currentSettings.enabledLocationIds, locationId];
    updateSettings({ enabledLocationIds: newIds });
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background flex-col">
      {/* Page Header - Full Width */}
      <div className="bg-card flex-shrink-0 border-b">
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <Shield className="h-6 w-6 text-primary" />
                Compliance Management
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Physical asset compliance management for UK HSE & British Standards
              </p>
            </div>
            {canManageCompliance && (
              <Button 
                onClick={() => setShowCreateDialog(true)}
                className="bg-primary hover:bg-primary/90 text-white flex-shrink-0"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Rule
              </Button>
            )}
          </div>
        </div>
      </div>



      {/* Content Area with Sidebar and Details */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - Regulation List */}
        <div className="w-80 bg-muted/30 flex flex-col overflow-hidden border-r-[3px]">
          {/* Search Input and Actions */}
          <div className="p-3 border-b bg-card space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search by code or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
              />
            </div>

          </div>
          
          <div className="flex-1 overflow-y-auto overflow-hidden">
            {/* Loading state for live mode */}
            {!isDemoMode && isLoading && (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
            
            {/* Live mode: API compliance checks */}
            {!isDemoMode && !isLoading && (
              <div className="p-3 space-y-2">
                {apiComplianceChecks.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                    <Shield className="h-12 w-12 text-muted-foreground/40 mb-4" />
                    <h3 className="font-semibold text-sm mb-1">No Rules Found</h3>
                    <p className="text-xs text-muted-foreground mb-4">
                      Get started by creating your first compliance rule
                    </p>
                    {canManageCompliance && (
                      <Button 
                        size="sm"
                        onClick={() => setShowCreateDialog(true)}
                        className="bg-primary hover:bg-primary/90"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        New Rule
                      </Button>
                    )}
                  </div>
                ) : (
                  apiComplianceChecks
                    .filter(check => 
                      check.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      check.type?.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map((check) => {
                      const isSelected = selectedCheckId === check.id;
                      const { interval, unit } = parseFrequency(check.frequency);

                      return (
                        <button
                          key={check.id}
                          onClick={() => handleSelectApiCheck(check.id)}
                          className={cn(
                            'w-full text-left p-3 rounded-lg border transition-all duration-200',
                            'hover:border-primary/50 hover:bg-accent/50 hover:shadow-md hover:scale-[1.02]',
                            isSelected
                              ? 'bg-primary/10 border-primary/50 ring-1 ring-primary/20'
                              : 'bg-card border-border'
                          )}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0 space-y-2">
                              <div>
                                <h3 className={cn(
                                  'font-semibold text-sm line-clamp-1',
                                  isSelected && 'text-primary'
                                )}>
                                  {check.name}
                                </h3>
                                <p className="text-xs text-muted-foreground font-mono mt-0.5">
                                  {check.type || 'Custom'}
                                </p>
                              </div>

                              {check.description && (
                                <p className="text-xs text-muted-foreground line-clamp-2">
                                  {check.description}
                                </p>
                              )}

                              <div className="flex items-center gap-2 text-xs">
                                <div className="flex items-center gap-1.5">
                                  <div className={cn(
                                    'w-2 h-2 rounded-full',
                                    check.active ? 'bg-green-600' : 'bg-slate-400'
                                  )} />
                                  <span className="text-muted-foreground">
                                    {check.active ? 'Active' : 'Inactive'}
                                  </span>
                                </div>
                                <Separator orientation="vertical" className="h-3" />
                                <div className="flex items-center gap-1.5">
                                  <Calendar className="h-3 w-3 text-muted-foreground" />
                                  <span className="text-muted-foreground">
                                    {interval} {unit}
                                  </span>
                                </div>
                              </div>
                            </div>
                            {isSelected && (
                              <ChevronRight className="h-4 w-4 text-primary flex-shrink-0 mt-1" />
                            )}
                          </div>
                        </button>
                      );
                    })
                )}
              </div>
            )}
            
            {/* Demo mode: Mock compliance regimes */}
            {isDemoMode && (
              <div className="p-3 space-y-2">
                {filteredRegimes.map((regime) => {
                  const settings = regimeSettings[regime.code];
                  const isSelected = selectedRegime === regime.code;

                  return (
                    <button
                      key={regime.code}
                      onClick={() => handleRegimeChange(regime.code)}
                      className={cn(
                        'w-full text-left p-3 rounded-lg border transition-all duration-200',
                        'hover:border-primary/50 hover:bg-accent/50 hover:shadow-md hover:scale-[1.02]',
                        isSelected
                          ? 'bg-primary/10 border-primary/50 ring-1 ring-primary/20'
                          : 'bg-card border-border'
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0 space-y-2">
                          <div>
                            <h3 className={cn(
                              'font-semibold text-sm line-clamp-1',
                              isSelected && 'text-primary'
                            )}>
                              {regime.code}
                            </h3>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {regime.name}
                            </p>
                          </div>

                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {regime.description}
                          </p>

                          <div className="flex items-center gap-2 text-xs">
                            <div className="flex items-center gap-1.5">
                              <div className={cn(
                                'w-2 h-2 rounded-full',
                                settings.enabled ? 'bg-green-600' : 'bg-slate-400'
                              )} />
                              <span className="text-muted-foreground">
                                {settings.enabled ? 'Enabled' : 'Disabled'}
                              </span>
                            </div>
                            <Separator orientation="vertical" className="h-3" />
                            <div className="flex items-center gap-1.5">
                              <Calendar className="h-3 w-3 text-muted-foreground" />
                              <span className="text-muted-foreground">
                                {settings.defaultInterval} {settings.intervalUnit}
                              </span>
                            </div>
                          </div>
                        </div>
                        {isSelected && (
                          <ChevronRight className="h-4 w-4 text-primary flex-shrink-0 mt-1" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Regulation Details */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Show placeholder if no item selected in live mode */}
          {!isDemoMode && !selectedCheckId ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center max-w-md">
                <Shield className="h-16 w-16 text-muted-foreground/40 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Rule Selected</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Select a compliance rule from the list to view and edit its details, or create a new rule to get started.
                </p>
                <Button 
                  onClick={() => setShowCreateDialog(true)}
                  className="bg-primary hover:bg-primary/90"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Rule
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Regulation Header */}
              <div className="border-b bg-card px-6 py-4 flex-shrink-0">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h2 className="text-lg font-semibold mb-1">
                      {isDemoMode ? currentRegime.name : (selectedApiCheck?.name || 'Unknown Rule')}
                    </h2>
                    <p className="text-sm text-muted-foreground font-mono">
                      {isDemoMode ? currentRegime.code : (selectedApiCheck?.type || 'Custom')}
                    </p>
                  </div>
                  {canManageCompliance && isDemoMode && (
                    <div className="flex items-center gap-2">
                      {hasChanges && (
                        <Button variant="outline" size="sm" onClick={handleReset}>
                          <X className="h-4 w-4 mr-1" />
                          Reset
                        </Button>
                      )}
                      <Button size="sm" onClick={handleSave} disabled={!hasChanges}>
                        <Save className="h-4 w-4 mr-1" />
                        Save Changes
                      </Button>
                      <Button variant="destructive" size="sm" onClick={handleDelete}>
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  )}
                  {canManageCompliance && !isDemoMode && selectedApiCheck && (
                    <div className="flex items-center gap-2">
                      {hasChanges && (
                        <Button variant="outline" size="sm" onClick={handleReset}>
                          <X className="h-4 w-4 mr-1" />
                          Reset
                        </Button>
                      )}
                      <Button size="sm" onClick={handleSave} disabled={!hasChanges || isSaving}>
                        {isSaving ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4 mr-1" />
                        )}
                        Save Changes
                      </Button>
                      <Button variant="destructive" size="sm" onClick={handleDelete}>
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  )}
                </div>
              </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
            <div className="border-t border-b bg-[rgb(242,245,245)] dark:bg-black flex-shrink-0">
              <div className="px-6 py-2">
                <TabsList className="h-9 bg-transparent p-0 gap-1">
                  <TabsTrigger 
                    value="details" 
                    className="px-2 py-1.5 text-sm data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:bg-[rgb(242,245,245)] data-[state=inactive]:dark:bg-black data-[state=inactive]:hover:bg-slate-200 data-[state=inactive]:dark:hover:bg-slate-800"
                  >
                    Details
                  </TabsTrigger>
                  <TabsTrigger 
                    value="categories" 
                    className="px-2 py-1.5 text-sm data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:bg-[rgb(242,245,245)] data-[state=inactive]:dark:bg-black data-[state=inactive]:hover:bg-slate-200 data-[state=inactive]:dark:hover:bg-slate-800"
                  >
                    Categories
                    <Badge variant="secondary" className="ml-2">
                      {currentSettings.enabledCategoryIds.length}
                    </Badge>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="locations" 
                    className="px-2 py-1.5 text-sm data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:bg-[rgb(242,245,245)] data-[state=inactive]:dark:bg-black data-[state=inactive]:hover:bg-slate-200 data-[state=inactive]:dark:hover:bg-slate-800"
                  >
                    Locations
                    <Badge variant="secondary" className="ml-2">
                      {currentSettings.enabledLocationIds.length}
                    </Badge>
                  </TabsTrigger>
                </TabsList>
              </div>
            </div>

            <div className="flex-1 overflow-auto">
              <div className="p-6">
              {/* Details Tab */}
              <TabsContent value="details" className="mt-0 space-y-6">
                {/* Basic Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Basic Information
                    </CardTitle>
                    <CardDescription>Core configuration settings for the compliance rule</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="font-semibold">Rule Code</Label>
                        <Input value={currentRegime.code} disabled className="bg-muted" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="rule-name" className="font-semibold">Rule Name *</Label>
                        <Input
                          id="rule-name"
                          value={editingName || currentRegime.name}
                          onChange={(e) => handleNameChange(e.target.value)}
                          placeholder="Enter rule name"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="compliance-type" className="font-semibold">Compliance Type *</Label>
                        <Select
                          value={currentSettings.complianceType}
                          onValueChange={(value: string) => updateSettings({ complianceType: value })}
                        >
                          <SelectTrigger id="compliance-type">
                            <SelectValue placeholder="Select compliance type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="safety-inspection">Safety inspection</SelectItem>
                            <SelectItem value="maintenance-requirement">Maintenance requirement</SelectItem>
                            <SelectItem value="certification-renewal">Certification/license renewal</SelectItem>
                            <SelectItem value="calibration-requirement">Calibration requirement</SelectItem>
                            <SelectItem value="regulatory-compliance">Regulatory compliance</SelectItem>
                            <SelectItem value="insurance-requirement">Insurance requirement</SelectItem>
                            <SelectItem value="electrical-safety">Electrical safety testing</SelectItem>
                            <SelectItem value="training-qualification">Training/operator qualification</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex items-center space-x-2 self-end pb-2">
                        <Switch
                          id="enable-rule"
                          checked={currentSettings.enabled}
                          onCheckedChange={(checked) => updateSettings({ enabled: checked })}
                        />
                        <Label htmlFor="enable-rule" className="font-normal cursor-pointer whitespace-nowrap">
                          Rule Enabled
                        </Label>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="interval" className="font-semibold">Default Inspection Interval *</Label>
                        <div className="flex gap-2">
                          <Input
                            id="interval"
                            type="number"
                            value={currentSettings.defaultInterval}
                            onChange={(e) => updateSettings({ defaultInterval: parseInt(e.target.value) || 0 })}
                            className="flex-1"
                          />
                          <Select
                            value={currentSettings.intervalUnit}
                            onValueChange={(value: IntervalUnit) => updateSettings({ intervalUnit: value })}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="days">Days</SelectItem>
                              <SelectItem value="months">Months</SelectItem>
                              <SelectItem value="years">Years</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="grace" className="font-semibold">Grace Period</Label>
                        <div className="flex gap-2">
                          <Input
                            id="grace"
                            type="number"
                            value={currentSettings.gracePeriod}
                            onChange={(e) => updateSettings({ gracePeriod: parseInt(e.target.value) || 0 })}
                            className="flex-1"
                          />
                          <Select
                            value={currentSettings.gracePeriodUnit}
                            onValueChange={(value: IntervalUnit) => updateSettings({ gracePeriodUnit: value })}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="days">Days</SelectItem>
                              <SelectItem value="months">Months</SelectItem>
                              <SelectItem value="years">Years</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="minValue" className="font-semibold">Minimum Asset Value</Label>
                        <Input
                          id="minValue"
                          type="number"
                          placeholder="No minimum"
                          value={currentSettings.minAssetValue ?? ''}
                          onChange={(e) => updateSettings({ minAssetValue: e.target.value ? parseFloat(e.target.value) : null })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="maxValue" className="font-semibold">Maximum Asset Value</Label>
                        <Input
                          id="maxValue"
                          type="number"
                          placeholder="No maximum"
                          value={currentSettings.maxAssetValue ?? ''}
                          onChange={(e) => updateSettings({ maxAssetValue: e.target.value ? parseFloat(e.target.value) : null })}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Regulation */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <BookOpen className="h-5 w-5" />
                      Regulation
                    </CardTitle>
                    <CardDescription>Reference information linking to official regulations</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="regBody" className="font-semibold">Regulatory Body</Label>
                      <Input
                        id="regBody"
                        value={currentSettings.regulatoryBody}
                        onChange={(e) => updateSettings({ regulatoryBody: e.target.value })}
                        placeholder="e.g., HSE (Health and Safety Executive)"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="legislation" className="font-semibold">Legislation</Label>
                      <Input
                        id="legislation"
                        value={currentSettings.legislation}
                        onChange={(e) => updateSettings({ legislation: e.target.value })}
                        placeholder="e.g., PUWER 1998 reg. 6"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="guidanceUrl" className="font-semibold">Guidance URL</Label>
                      <div className="flex gap-2">
                        <Input
                          id="guidanceUrl"
                          value={currentSettings.guidanceUrl}
                          onChange={(e) => updateSettings({ guidanceUrl: e.target.value })}
                          placeholder="https://..."
                          className="flex-1"
                        />
                        {currentSettings.guidanceUrl && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(currentSettings.guidanceUrl, '_blank')}
                          >
                            <ExternalLink className="h-4 w-4 mr-1" />
                            Open
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Acceptance Requirements */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5" />
                      Acceptance Requirements
                    </CardTitle>
                    <CardDescription>Define what information must be provided when completing compliance checks</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label className="font-semibold">Document Upload (Certificate)</Label>
                        <p className="text-sm text-muted-foreground">Require upload of compliance certificate</p>
                      </div>
                      <Switch
                        checked={currentSettings.requireCertificate}
                        onCheckedChange={(checked) => updateSettings({ requireCertificate: checked })}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label className="font-semibold">Document Upload (Report)</Label>
                        <p className="text-sm text-muted-foreground">Require upload of inspection or test report</p>
                      </div>
                      <Switch
                        checked={currentSettings.requireReport}
                        onCheckedChange={(checked) => updateSettings({ requireReport: checked })}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label className="font-semibold">Photo Evidence</Label>
                        <p className="text-sm text-muted-foreground">Require photographic evidence of completion</p>
                      </div>
                      <Switch
                        checked={currentSettings.requirePhoto}
                        onCheckedChange={(checked) => updateSettings({ requirePhoto: checked })}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label className="font-semibold">Notes</Label>
                        <p className="text-sm text-muted-foreground">Require written notes or comments</p>
                      </div>
                      <Switch
                        checked={currentSettings.requireNotes}
                        onCheckedChange={(checked) => updateSettings({ requireNotes: checked })}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label className="font-semibold">Contractor Details</Label>
                        <p className="text-sm text-muted-foreground">Require information about contractor or service provider</p>
                      </div>
                      <Switch
                        checked={currentSettings.requireContractorDetails}
                        onCheckedChange={(checked) => updateSettings({ requireContractorDetails: checked })}
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="categories" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold">Asset Categories</h3>
                      <p className="text-sm text-muted-foreground">
                        Select which asset categories this compliance rule applies to
                      </p>
                    </div>
                    <Badge variant="secondary">
                      {currentSettings.enabledCategoryIds.length} selected
                    </Badge>
                  </div>

                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search categories..."
                      value={categorySearch}
                      onChange={(e) => setCategorySearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>

                  <div className="border rounded-lg divide-y max-h-[400px] overflow-y-auto">
                    {filteredCategories.map((category) => (
                      <div key={category.id} className="flex items-center justify-between p-3 hover:bg-muted/50">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${category.color}`} />
                          <span className="text-sm font-medium">{category.name}</span>
                          {currentSettings.enabledCategoryIds.includes(category.id) && (
                            <Check className="h-4 w-4 text-primary" />
                          )}
                        </div>
                        <Switch
                          checked={currentSettings.enabledCategoryIds.includes(category.id)}
                          onCheckedChange={() => toggleCategory(category.id)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Locations Tab */}
              <TabsContent value="locations" className="space-y-4 mt-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Package className="h-5 w-5" />
                          Asset Categories
                        </CardTitle>
                        <CardDescription>
                          Select which asset categories this compliance rule applies to
                        </CardDescription>
                      </div>
                      <Badge variant="secondary" className="text-sm">
                        {currentSettings.enabledCategoryIds.length} selected
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search categories..."
                          value={categorySearch}
                          onChange={(e) => setCategorySearch(e.target.value)}
                          className="pl-9"
                        />
                      </div>

                      <Separator />

                      <div className="space-y-1">
                        {filteredCategories.map((category) => {
                          const isEnabled = currentSettings.enabledCategoryIds.includes(category.id);
                          const isParent = !category.parentId;
                          const isChild = !!category.parentId;

                          return (
                            <div
                              key={category.id}
                              className={cn(
                                'flex items-center justify-between p-3 rounded-lg border transition-all',
                                isEnabled ? 'bg-primary/5 border-primary/20' : 'bg-card border-border hover:bg-accent',
                                isChild && 'ml-6'
                              )}
                            >
                              <div className="flex items-center gap-3 flex-1">
                                <Switch
                                  checked={isEnabled}
                                  onCheckedChange={() => toggleCategory(category.id)}
                                />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className={cn('text-sm font-medium', isParent && 'font-semibold')}>
                                      {category.name}
                                    </span>
                                    {isEnabled && (
                                      <CheckCircle2 className="h-4 w-4 text-primary" />
                                    )}
                                  </div>
                                </div>
                              </div>
                              <Badge variant="outline" className="ml-2">
                                {category.assetCount} assets
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Locations Tab */}
              <TabsContent value="locations" className="mt-0">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Building2 className="h-5 w-5" />
                          Locations
                        </CardTitle>
                        <CardDescription>
                          Select which locations this compliance rule applies to
                        </CardDescription>
                      </div>
                      <Badge variant="secondary" className="text-sm">
                        {currentSettings.enabledLocationIds.length} selected
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search locations..."
                          value={locationSearch}
                          onChange={(e) => setLocationSearch(e.target.value)}
                          className="pl-9"
                        />
                      </div>

                      <Separator />

                      <div className="space-y-1">
                        {filteredLocations.map((location) => {
                          const isEnabled = currentSettings.enabledLocationIds.includes(location.id);
                          const isParent = !location.parentId;
                          const isChild = !!location.parentId;

                          return (
                            <div
                              key={location.id}
                              className={cn(
                                'flex items-center justify-between p-3 rounded-lg border transition-all',
                                isEnabled ? 'bg-primary/5 border-primary/20' : 'bg-card border-border hover:bg-accent',
                                isChild && 'ml-6'
                              )}
                            >
                              <div className="flex items-center gap-3 flex-1">
                                <Switch
                                  checked={isEnabled}
                                  onCheckedChange={() => toggleLocation(location.id)}
                                />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={cn('text-sm font-medium', isParent && 'font-semibold')}>
                                      {location.name}
                                    </span>
                                    {isEnabled && (
                                      <CheckCircle2 className="h-4 w-4 text-primary" />
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground">{location.address}</p>
                                </div>
                              </div>
                              <Badge variant="outline" className="ml-2">
                                {location.assetCount} assets
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              </div>
            </div>
          </Tabs>
          </>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unsaved Changes</DialogTitle>
            <DialogDescription>
              You have unsaved changes. Would you like to save them before switching to another regulation?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleCancelChange}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDiscardChanges}>
              Discard Changes
            </Button>
            <Button onClick={handleConfirmSave}>
              Save & Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Regulation</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{isDemoMode ? currentRegime.name : selectedApiCheck?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Regulation'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create New Compliance Check Dialog (Live Mode Only) */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0 pb-0">
            <DialogTitle className="text-2xl flex items-center gap-2">
              <Shield className="h-6 w-6 text-primary" />
              Create New Rule
            </DialogTitle>
            <DialogDescription className="text-left">
              Configure a new compliance rule with all necessary details. All fields can be edited later.
            </DialogDescription>
          </DialogHeader>
          
          <Tabs value={newCheckActiveTab} onValueChange={setNewCheckActiveTab} className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-shrink-0 px-6 pt-4">
              <TabsList className="grid w-full grid-cols-3 bg-[rgb(242,245,245)] p-1 rounded-md">
                <TabsTrigger 
                  value="details" 
                  className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=inactive]:bg-transparent data-[state=inactive]:hover:bg-[rgb(232,235,235)]"
                >
                  Details
                </TabsTrigger>
                <TabsTrigger 
                  value="categories" 
                  className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=inactive]:bg-transparent data-[state=inactive]:hover:bg-[rgb(232,235,235)]"
                >
                  Categories
                  <Badge variant="secondary" className="ml-2">
                    {newCheckCategoryIds.length}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger 
                  value="locations" 
                  className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=inactive]:bg-transparent data-[state=inactive]:hover:bg-[rgb(232,235,235)]"
                >
                  Locations
                  <Badge variant="secondary" className="ml-2">
                    {newCheckLocationIds.length}
                  </Badge>
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="flex-1 overflow-y-auto px-1">
              <TabsContent value="details" className="mt-0 px-5 py-6 space-y-6">
              {/* Basic Information Section */}
              <div>
                <div className="mb-4">
                  <h3 className="text-base font-semibold flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    Basic Information
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">Core details of the compliance rule</p>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="new-check-type" className="text-sm font-semibold">
                        Rule Code <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="new-check-type"
                        value={newCheckType}
                        onChange={(e) => setNewCheckType(e.target.value)}
                        placeholder="e.g., PUWER, LOLER, PAT"
                        className="h-10"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="new-check-name" className="text-sm font-semibold">
                        Rule Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="new-check-name"
                        value={newCheckName}
                        onChange={(e) => setNewCheckName(e.target.value)}
                        placeholder="e.g., Annual Fire Safety Inspection"
                        className="h-10"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="new-check-description" className="text-sm font-semibold">
                      Description
                    </Label>
                    <Input
                      id="new-check-description"
                      value={newCheckDescription}
                      onChange={(e) => setNewCheckDescription(e.target.value)}
                      placeholder="Brief description of the compliance rule"
                      className="h-10"
                    />
                  </div>

                  <div className="flex gap-4 items-end">
                    <div className="flex-1 space-y-2">
                      <Label htmlFor="new-check-compliance-type" className="text-sm font-semibold">
                        Compliance Type <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={newCheckComplianceType}
                        onValueChange={setNewCheckComplianceType}
                      >
                        <SelectTrigger id="new-check-compliance-type" className="h-10">
                          <SelectValue placeholder="Select compliance type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="safety-inspection">Safety inspection</SelectItem>
                          <SelectItem value="maintenance-requirement">Maintenance requirement</SelectItem>
                          <SelectItem value="certification-renewal">Certification/license renewal</SelectItem>
                          <SelectItem value="calibration-requirement">Calibration requirement</SelectItem>
                          <SelectItem value="regulatory-compliance">Regulatory compliance</SelectItem>
                          <SelectItem value="insurance-requirement">Insurance requirement</SelectItem>
                          <SelectItem value="electrical-safety">Electrical safety testing</SelectItem>
                          <SelectItem value="training-qualification">Training/operator qualification</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center space-x-2 pb-2">
                      <Switch
                        id="new-check-enabled"
                        checked={newCheckEnabled}
                        onCheckedChange={setNewCheckEnabled}
                      />
                      <Label htmlFor="new-check-enabled" className="font-normal cursor-pointer text-sm whitespace-nowrap">
                        Rule Enabled
                      </Label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <Separator />

              {/* Inspection Schedule Section */}
              <div>
                <div className="mb-4">
                  <h3 className="text-base font-semibold flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" />
                    Inspection Schedule
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">Define when and how often inspections occur</p>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="new-check-interval" className="text-sm font-semibold">
                        Inspection Interval <span className="text-destructive">*</span>
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          id="new-check-interval"
                          type="number"
                          value={newCheckInterval}
                          onChange={(e) => setNewCheckInterval(parseInt(e.target.value) || 1)}
                          className="flex-1 h-10"
                          min="1"
                        />
                        <Select
                          value={newCheckIntervalUnit}
                          onValueChange={(value: IntervalUnit) => setNewCheckIntervalUnit(value)}
                        >
                          <SelectTrigger className="w-32 h-10">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="days">Days</SelectItem>
                            <SelectItem value="months">Months</SelectItem>
                            <SelectItem value="years">Years</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="new-check-lead-time" className="text-sm font-semibold">
                        Grace Period
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          id="new-check-lead-time"
                          type="number"
                          value={newCheckLeadTime}
                          onChange={(e) => setNewCheckLeadTime(parseInt(e.target.value) || 0)}
                          min="0"
                          className="flex-1 h-10"
                        />
                        <Select
                          value={newCheckLeadTimeUnit}
                          onValueChange={(value: IntervalUnit) => setNewCheckLeadTimeUnit(value)}
                        >
                          <SelectTrigger className="w-32 h-10">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="days">Days</SelectItem>
                            <SelectItem value="months">Months</SelectItem>
                            <SelectItem value="years">Years</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="new-check-priority" className="text-sm font-semibold">
                      Priority Level
                    </Label>
                    <Select
                      value={newCheckPriority}
                      onValueChange={(value: string) => setNewCheckPriority(value)}
                    >
                      <SelectTrigger id="new-check-priority" className="h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-blue-500" />
                            Low Priority
                          </div>
                        </SelectItem>
                        <SelectItem value="medium">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-yellow-500" />
                            Medium Priority
                          </div>
                        </SelectItem>
                        <SelectItem value="high">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-orange-500" />
                            High Priority
                          </div>
                        </SelectItem>
                        <SelectItem value="critical">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-red-500" />
                            Critical Priority
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <Separator />

              {/* Regulatory Information Section */}
              <div>
                <div className="mb-4">
                  <h3 className="text-base font-semibold flex items-center gap-2">
                    <Shield className="h-4 w-4 text-primary" />
                    Regulatory Information
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">Reference to official regulations and standards</p>
                </div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-check-regulatory-body" className="text-sm font-semibold">
                      Regulatory Body
                    </Label>
                    <Input
                      id="new-check-regulatory-body"
                      value={newCheckRegulatoryBody}
                      onChange={(e) => setNewCheckRegulatoryBody(e.target.value)}
                      placeholder="e.g., HSE (Health and Safety Executive)"
                      className="h-10"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="new-check-legislation" className="text-sm font-semibold">
                      Legislation Reference
                    </Label>
                    <Input
                      id="new-check-legislation"
                      value={newCheckLegislation}
                      onChange={(e) => setNewCheckLegislation(e.target.value)}
                      placeholder="e.g., PUWER 1998 reg. 6"
                      className="h-10"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="new-check-guidance-url" className="text-sm font-semibold">
                      Guidance URL
                    </Label>
                    <Input
                      id="new-check-guidance-url"
                      value={newCheckGuidanceUrl}
                      onChange={(e) => setNewCheckGuidanceUrl(e.target.value)}
                      placeholder="https://..."
                      className="h-10"
                    />
                  </div>
                </div>
              </div>

              {/* Divider */}
              <Separator />

              {/* Asset Value Filters Section */}
              <div>
                <div className="mb-4">
                  <h3 className="text-base font-semibold flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-primary" />
                    Asset Value Filters
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">Optional filters based on asset value</p>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="new-check-min-value" className="text-sm font-semibold">
                        Minimum Asset Value
                      </Label>
                      <Input
                        id="new-check-min-value"
                        type="number"
                        value={newCheckMinValue ?? ''}
                        onChange={(e) => setNewCheckMinValue(e.target.value ? parseFloat(e.target.value) : null)}
                        placeholder="No minimum"
                        className="h-10"
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="new-check-max-value" className="text-sm font-semibold">
                        Maximum Asset Value
                      </Label>
                      <Input
                        id="new-check-max-value"
                        type="number"
                        value={newCheckMaxValue ?? ''}
                        onChange={(e) => setNewCheckMaxValue(e.target.value ? parseFloat(e.target.value) : null)}
                        placeholder="No maximum"
                        className="h-10"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <Separator />

              {/* Acceptance Requirements Section */}
              <div>
                <div className="mb-4">
                  <h3 className="text-base font-semibold flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    Acceptance Requirements
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">Define what information must be provided when completing compliance checks</p>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-sm font-semibold">Document Upload (Certificate)</Label>
                      <p className="text-xs text-muted-foreground">Require upload of compliance certificate</p>
                    </div>
                    <Switch
                      checked={newCheckRequireCertificate}
                      onCheckedChange={setNewCheckRequireCertificate}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-sm font-semibold">Document Upload (Report)</Label>
                      <p className="text-xs text-muted-foreground">Require upload of inspection or test report</p>
                    </div>
                    <Switch
                      checked={newCheckRequireReport}
                      onCheckedChange={setNewCheckRequireReport}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-sm font-semibold">Photo Evidence</Label>
                      <p className="text-xs text-muted-foreground">Require photographic evidence of completion</p>
                    </div>
                    <Switch
                      checked={newCheckRequirePhoto}
                      onCheckedChange={setNewCheckRequirePhoto}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-sm font-semibold">Notes</Label>
                      <p className="text-xs text-muted-foreground">Require written notes or comments</p>
                    </div>
                    <Switch
                      checked={newCheckRequireNotes}
                      onCheckedChange={setNewCheckRequireNotes}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-sm font-semibold">Contractor Details</Label>
                      <p className="text-xs text-muted-foreground">Require information about contractor or service provider</p>
                    </div>
                    <Switch
                      checked={newCheckRequireContractorDetails}
                      onCheckedChange={setNewCheckRequireContractorDetails}
                    />
                  </div>
                </div>
              </div>
              </TabsContent>

              {/* Categories Tab */}
              <TabsContent value="categories" className="mt-0 px-5 py-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-base font-semibold flex items-center gap-2">
                      <Package className="h-4 w-4 text-primary" />
                      Asset Categories
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Select which asset categories this compliance rule applies to
                    </p>
                  </div>

                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search categories..."
                      value={newCheckCategorySearch}
                      onChange={(e) => setNewCheckCategorySearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>

                  <Separator />

                  <div className="space-y-1 max-h-[400px] overflow-y-auto">
                    {MOCK_CATEGORIES
                      .filter(cat => 
                        cat.name.toLowerCase().includes(newCheckCategorySearch.toLowerCase())
                      )
                      .map((category) => {
                        const isEnabled = newCheckCategoryIds.includes(category.id);
                        const isChild = !!category.parentId;

                        return (
                          <div
                            key={category.id}
                            className={cn(
                              'flex items-center justify-between p-3 rounded-lg border transition-all',
                              isEnabled ? 'bg-primary/5 border-primary/20' : 'bg-card border-border hover:bg-accent',
                              isChild && 'ml-6'
                            )}
                          >
                            <div className="flex items-center gap-3 flex-1">
                              <Switch
                                checked={isEnabled}
                                onCheckedChange={() => {
                                  if (isEnabled) {
                                    setNewCheckCategoryIds(newCheckCategoryIds.filter(id => id !== category.id));
                                  } else {
                                    setNewCheckCategoryIds([...newCheckCategoryIds, category.id]);
                                  }
                                }}
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium">
                                    {category.name}
                                  </span>
                                  {isEnabled && (
                                    <CheckCircle2 className="h-4 w-4 text-primary" />
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </TabsContent>

              {/* Locations Tab */}
              <TabsContent value="locations" className="mt-0 px-5 py-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-base font-semibold flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-primary" />
                      Locations
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Select which locations this compliance rule applies to
                    </p>
                  </div>

                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search locations..."
                      value={newCheckLocationSearch}
                      onChange={(e) => setNewCheckLocationSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>

                  <Separator />

                  <div className="space-y-1 max-h-[400px] overflow-y-auto">
                    {MOCK_LOCATIONS
                      .filter(loc => 
                        loc.name.toLowerCase().includes(newCheckLocationSearch.toLowerCase()) ||
                        loc.address?.toLowerCase().includes(newCheckLocationSearch.toLowerCase())
                      )
                      .map((location) => {
                        const isEnabled = newCheckLocationIds.includes(location.id);

                        return (
                          <div
                            key={location.id}
                            className={cn(
                              'flex items-center justify-between p-3 rounded-lg border transition-all',
                              isEnabled ? 'bg-primary/5 border-primary/20' : 'bg-card border-border hover:bg-accent'
                            )}
                          >
                            <div className="flex items-center gap-3 flex-1">
                              <Switch
                                checked={isEnabled}
                                onCheckedChange={() => {
                                  if (isEnabled) {
                                    setNewCheckLocationIds(newCheckLocationIds.filter(id => id !== location.id));
                                  } else {
                                    setNewCheckLocationIds([...newCheckLocationIds, location.id]);
                                  }
                                }}
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium">
                                    {location.name}
                                  </span>
                                  {isEnabled && (
                                    <CheckCircle2 className="h-4 w-4 text-primary" />
                                  )}
                                </div>
                                {location.address && (
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    {location.address}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </TabsContent>
            </div>
          </Tabs>

          <DialogFooter className="flex-shrink-0 gap-2 sm:gap-0 border-t pt-4">
            <Button 
              variant="outline" 
              onClick={() => { 
                setShowCreateDialog(false); 
                setNewCheckName('');
                setNewCheckType('');
                setNewCheckDescription('');
                setNewCheckInterval(12);
                setNewCheckIntervalUnit('months');
                setNewCheckLeadTime(30);
                setNewCheckLeadTimeUnit('days');
                setNewCheckPriority('medium');
                setNewCheckRegulatoryBody('');
                setNewCheckLegislation('');
                setNewCheckGuidanceUrl('');
                setNewCheckMinValue(null);
                setNewCheckMaxValue(null);
                setNewCheckEnabled(true);
                setNewCheckComplianceType('safety-inspection');
                setNewCheckRequireCertificate(false);
                setNewCheckRequireReport(false);
                setNewCheckRequirePhoto(false);
                setNewCheckRequireNotes(false);
                setNewCheckRequireCostEstimate(false);
                setNewCheckRequireContractorDetails(false);
                setNewCheckRequireNextDueDate(true);
                setNewCheckActiveTab('details');
                setNewCheckCategoryIds([]);
                setNewCheckLocationIds([]);
                setNewCheckCategorySearch('');
                setNewCheckLocationSearch('');
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateCheck} 
              disabled={isSaving || !newCheckName.trim()}
              className="bg-primary hover:bg-primary/90 text-white"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating Rule...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Rule
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
